// ============================================================================
// AffectedJobsPanel Component
// ============================================================================
// Shared component for displaying affected jobs in wizard issue resolution steps

import { Box, Typography, Button, IconButton } from "@mui/material";
import { Visibility, VisibilityOff, Delete, InsertDriveFileOutlined } from "@mui/icons-material";
import { BOUNDARY_COLORS } from "../../shared/constants";
import type { Job } from "../../../../../../api/types";
import "./AffectedJobsPanel.css";
import PortalTooltip from "../../../../../helpers/PortalTooltip";

// ============================================================================
// Types
// ============================================================================

export interface AffectedJobsPanelProps {
	/** List of jobs affected by the current issue */
	jobs: Job[];
	/** Set of job IDs that are currently hidden on the map */
	hiddenJobIds: Set<string>;
	/** Toggle visibility of a single job */
	onToggleVisibility: (jobId: string) => void;
	/** Show all jobs on the map */
	onShowAll: () => void;
	/** Hide all jobs on the map */
	onHideAll: () => void;
	/** Handle delete button click for a job */
	onDeleteJob: (jobId: string) => void;
	/** Get list of source files for a job */
	getJobSourceFiles: (jobId: string) => string[];
}

// ============================================================================
// Component
// ============================================================================

/**
 * Displays a panel with affected jobs for wizard issue resolution steps.
 * Includes visibility toggles, color swatches matching map colors, and delete buttons.
 */
export function AffectedJobsPanel({
	jobs,
	hiddenJobIds,
	onToggleVisibility,
	onShowAll,
	onHideAll,
	onDeleteJob,
	getJobSourceFiles,
}: AffectedJobsPanelProps) {
	return (
		<Box className="affected-jobs-panel">
			{/* Header */}
			<Box className="legend-header">
				<Typography variant="subtitle2">Affected Jobs ({jobs.length})</Typography>
				<Box className="legend-controls">
					<Button size="small" variant="text" onClick={onShowAll} disabled={hiddenJobIds.size === 0}>
						Show All
					</Button>
					<Button size="small" variant="text" onClick={onHideAll} disabled={hiddenJobIds.size === jobs.length}>
						Hide All
					</Button>
				</Box>
			</Box>

			{/* Job List */}
			<Box className="job-list">
				{jobs.map((job, index) => {
					const isVisible = !hiddenJobIds.has(job.id);
					const color = BOUNDARY_COLORS[index % BOUNDARY_COLORS.length];
					const sourceFiles = getJobSourceFiles(job.id);

					return (
						<Box
							key={job.id}
							className={`job-item ${isVisible ? "" : "hidden"}`}
							sx={{
								borderBottom: index < jobs.length - 1 ? 1 : 0,
								borderColor: "divider",
							}}
						>
							{/* Color Swatch */}
							<Box
								className={`color-swatch ${isVisible ? "" : "hidden"}`}
								sx={{
									bgcolor: color,
									borderColor: isVisible ? color : undefined,
								}}
							/>

							{/* Visibility Toggle */}
							<PortalTooltip title={isVisible ? "Hide on map" : "Show on map"}>
								<IconButton size="small" onClick={() => onToggleVisibility(job.id)} className="visibility-toggle">
									{isVisible ? (
										<Visibility fontSize="small" color="primary" />
									) : (
										<VisibilityOff fontSize="small" color="disabled" />
									)}
								</IconButton>
							</PortalTooltip>

							{/* Job Info */}
							<Box className="job-info">
								<Typography variant="body2" fontWeight={500} noWrap>
									{job.growerName || job.importGrowerName}
								</Typography>
								<Typography variant="body2" color="text.secondary" noWrap>
									{job.farmName || job.importFarmName} &bull; {job.fieldName || job.importFieldName}
								</Typography>
								{/* Source Files */}
								{sourceFiles.length > 0 && (
									<Box className="source-files">
										<InsertDriveFileOutlined className="source-files-icon" />
										<Typography variant="caption" color="text.secondary" noWrap>
											{sourceFiles.join(", ")}
										</Typography>
									</Box>
								)}
							</Box>

							{/* Delete Button */}
							<PortalTooltip title="Delete this job">
								<IconButton
									data-testid={`delete-job-button-${job.id}`}
									size="small"
									color="error"
									onClick={() => onDeleteJob(job.id)}
								>
									<Delete fontSize="small" />
								</IconButton>
							</PortalTooltip>
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}
